import * as THREE from 'three';
import { DebugWidget, createWidgetContainer } from './DebugOverlay';
import type { Viewer } from '../viewer/viewer';
import type { FloorplanPersona } from '../personas/personas/implementations/FloorplanPersona';
import type { PointoramaFloorMesh3D, MeshPart } from './PointoramaFloorMesh3D';

/**
 * Debug widget for the currently selected floor's 3D mesh:
 *  - export it as a Wavefront OBJ (OBJ is the simplest exporter — synchronous,
 *    text-based, no async/blob juggling), and
 *  - toggle "colorize by id", which tints each provenance group (shell pieces
 *    + door/window openings) a distinct color so the per-id attribution carried
 *    through the Manifold boolean is visible.
 *
 * The whole widget is hidden unless the floorplan persona is active, and the
 * controls are only enabled when a floor is selected (via that persona) AND the
 * floor has a built 3D mesh in the scene.
 */
export class FloorExportDebugWidget implements DebugWidget {
  name = 'floor-export';
  container: HTMLElement;
  private readonly getViewer: () => Viewer | undefined;
  private readonly statusEl: HTMLElement;
  private readonly exportButton: HTMLButtonElement;
  private readonly colorizeCheckbox: HTMLInputElement;

  constructor(getViewer: () => Viewer | undefined) {
    this.getViewer = getViewer;
    this.container = createWidgetContainer('📦 Floor Export');

    const content = document.createElement('div');
    content.style.cssText = 'line-height: 1.6;';

    this.statusEl = document.createElement('div');
    this.statusEl.style.cssText = 'margin-bottom: 8px; font-size: 12px; color: rgba(255, 255, 255, 0.7);';
    this.statusEl.textContent = 'No floor selected';

    this.exportButton = document.createElement('button');
    this.exportButton.textContent = 'Export floor (.obj)';
    this.exportButton.style.cssText = `
      width: 100%;
      padding: 6px 10px;
      background: #4CAF50;
      color: #fff;
      border: none;
      border-radius: 4px;
      cursor: pointer;
      font-size: 12px;
    `;
    this.exportButton.addEventListener('click', () => this.exportSelectedFloor());

    // Colorize-by-id toggle.
    const colorizeLabel = document.createElement('label');
    colorizeLabel.style.cssText = 'display: flex; align-items: center; gap: 6px; margin-top: 8px; font-size: 12px; cursor: pointer;';
    this.colorizeCheckbox = document.createElement('input');
    this.colorizeCheckbox.type = 'checkbox';
    this.colorizeCheckbox.addEventListener('change', () => {
      this.getSelectedFloorMesh()?.setDebugColorizeGroups(this.colorizeCheckbox.checked);
    });
    colorizeLabel.appendChild(this.colorizeCheckbox);
    colorizeLabel.appendChild(document.createTextNode('Colorize faces by id (shell / rooms / openings)'));

    content.appendChild(this.statusEl);
    content.appendChild(this.exportButton);
    content.appendChild(colorizeLabel);
    this.container.appendChild(content);
  }

  update(): void {
    const viewer = this.getViewer();

    // Only show this widget in the floorplan persona.
    const personaActive = viewer?.personaManager.isPersonaActive('floorplan') ?? false;
    this.container.style.display = personaActive ? '' : 'none';
    if (!personaActive) return;

    const mesh = this.getSelectedFloorMesh();
    const enabled = mesh !== null;

    this.exportButton.disabled = !enabled;
    this.exportButton.style.opacity = enabled ? '1' : '0.4';
    this.exportButton.style.cursor = enabled ? 'pointer' : 'not-allowed';
    this.colorizeCheckbox.disabled = !enabled;
    // Reflect the selected floor's current colorize state (it may differ per
    // floor, and survives rebuilds).
    this.colorizeCheckbox.checked = mesh?.debugColorizeGroups ?? false;

    if (enabled) {
      const ids = mesh.groupIds;
      // Non-shell groups are rooms (cutout walls) + openings (reveal faces).
      const tagged = ids.filter((t) => t !== '__shell__' && !t.startsWith('__shell__#')).length;
      this.statusEl.textContent = `Floor ${mesh.identifier} — ${ids.length} groups (${tagged} room/opening)`;
      this.statusEl.style.color = '#4CAF50';
    } else {
      const persona = viewer?.personaManager.getPersona('floorplan') as FloorplanPersona | undefined;
      this.statusEl.textContent = persona?.getSelectedFloorGroupIdentifier()
        ? 'Selected floor has no 3D mesh'
        : 'No floor selected';
      this.statusEl.style.color = 'rgba(255, 255, 255, 0.7)';
    }
  }

  destroy(): void {
    // No cleanup needed
  }

  /**
   * Resolve the THREE object of the currently selected floor, or null when no
   * floor is selected or the selection has no built 3D mesh yet.
   */
  private getSelectedFloorMesh(): PointoramaFloorMesh3D | null {
    const viewer = this.getViewer();
    if (!viewer) return null;

    const persona = viewer.personaManager.getPersona('floorplan') as FloorplanPersona | undefined;
    const floorId = persona?.getSelectedFloorGroupIdentifier();
    if (!floorId) return null;

    return viewer.scene.floorMeshes3D.find((m) => m.identifier === floorId) ?? null;
  }

  private exportSelectedFloor(): void {
    const floorMesh = this.getSelectedFloorMesh();
    if (!floorMesh) return;

    // Emit an OBJ that preserves the per-part linkage: one `g <kind>:<id>`
    // group per mesh part (shell / room / opening), so the source of every
    // face is recoverable in the exported file. (The stock OBJExporter
    // flattens groups, losing this.)
    const objText = exportGroupedObj(floorMesh.getMeshGeometry(), floorMesh.meshParts, floorMesh.meshOrigin);
    downloadText(objText, `floor-${floorMesh.identifier}.obj`, 'text/plain');
  }
}

/** Build a Wavefront OBJ with one named group per {@link MeshPart}, so each
 * face traces back to its shell/room/opening source. */
// The geometry is relative to `origin` (float32 precision); the file gets world coordinates back.
function exportGroupedObj(geometry: THREE.BufferGeometry | null, parts: MeshPart[], origin: { x: number; y: number }): string {
  if (!geometry) return '';
  const pos = geometry.getAttribute('position');
  const index = geometry.getIndex();
  const lines: string[] = ['# Pointorama floor export (grouped by source)'];

  // Vertices (1-based in OBJ).
  for (let v = 0; v < pos.count; v++) {
    lines.push(`v ${pos.getX(v) + origin.x} ${pos.getY(v) + origin.y} ${pos.getZ(v)}`);
  }

  const vertOf = (i: number): number => (index ? index.getX(i) : i);
  for (const part of parts) {
    const name = part.kind === 'shell' ? 'shell' : `${part.kind}:${part.id}`;
    lines.push(`g ${name}`);
    for (let t = part.triStart; t < part.triStart + part.triCount; t++) {
      const a = vertOf(t * 3) + 1;
      const b = vertOf(t * 3 + 1) + 1;
      const c = vertOf(t * 3 + 2) + 1;
      lines.push(`f ${a} ${b} ${c}`);
    }
  }
  return lines.join('\n');
}

function downloadText(text: string, filename: string, mimeType: string): void {
  const blob = new Blob([text], { type: mimeType });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}
